import React from "react"

import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@coval/ui"

import {
  isWebSocketConnectionMode,
  SimulatorType,
  WebSocketConnectionMode,
} from "@/types/api/SimulatorType"

import {
  AgentTestConnectionButton,
  type AgentTestConnectionConfig,
} from "../AgentTestConnectionButton"
import {
  BehaviorFields,
  ChatWebSocketDirectModeFields,
  ChatWebSocketHandshakeFields,
  ChatWebSocketHttpFirstConnectionFields,
  ChatWebSocketHttpFirstSetupFields,
  TextMessageFormatFields,
} from "./chat-websocket-config"
import type { ChatWebSocketSimulatorConfigData } from "./chat-websocket-config/types"
import { ConnectionModeToggle } from "./websocket-config"

interface ChatWebSocketSimulatorConfigProps {
  simulatorType: SimulatorType
  simulationMetadata: ChatWebSocketSimulatorConfigData
  onMetadataUpdate: (
    metadata: Partial<ChatWebSocketSimulatorConfigData>,
  ) => void
  disabled?: boolean
  testConnection?: AgentTestConnectionConfig | null
  hideAdvancedSettings?: boolean
}

export const ChatWebSocketAdvancedFields: React.FC<{
  metadata: ChatWebSocketSimulatorConfigData
  onMetadataUpdate: (
    metadata: Partial<ChatWebSocketSimulatorConfigData>,
  ) => void
  disabled?: boolean
  includeBehavior?: boolean
}> = ({ metadata, onMetadataUpdate, disabled, includeBehavior = false }) => (
  <div className="space-y-8">
    {includeBehavior && (
      <BehaviorFields
        metadata={metadata}
        onMetadataUpdate={onMetadataUpdate}
        disabled={disabled}
      />
    )}
    <TextMessageFormatFields
      metadata={metadata}
      onMetadataUpdate={onMetadataUpdate}
      disabled={disabled}
    />
    <ChatWebSocketHandshakeFields
      metadata={metadata}
      onMetadataUpdate={onMetadataUpdate}
      disabled={disabled}
    />
  </div>
)

export const ChatWebSocketSimulatorConfig: React.FC<
  ChatWebSocketSimulatorConfigProps
> = ({
  simulatorType,
  simulationMetadata,
  onMetadataUpdate,
  disabled = false,
  testConnection,
  hideAdvancedSettings = false,
}) => {
  const connectionMode = isWebSocketConnectionMode(
    simulationMetadata.connection_mode,
  )
    ? simulationMetadata.connection_mode
    : WebSocketConnectionMode.DIRECT
  const isHttpFirst = connectionMode === WebSocketConnectionMode.HTTP_FIRST

  if (simulatorType !== SimulatorType.CHAT_WEBSOCKET) {
    return null
  }

  return (
    <div className="space-y-4" data-testid="chat-websocket-config">
      <ConnectionModeToggle
        connectionMode={connectionMode}
        onConnectionModeChange={(mode) => {
          onMetadataUpdate({ connection_mode: mode })
        }}
        disabled={disabled}
      />

      {testConnection && (
        <AgentTestConnectionButton
          agentId={testConnection.agentId}
          disabled={testConnection.disabled}
          disabledReason={testConnection.disabledReason}
        />
      )}

      {!isHttpFirst && (
        <ChatWebSocketDirectModeFields
          metadata={simulationMetadata}
          onMetadataUpdate={onMetadataUpdate}
          disabled={disabled}
        />
      )}

      {isHttpFirst && (
        <>
          <ChatWebSocketHttpFirstSetupFields
            metadata={simulationMetadata}
            onMetadataUpdate={onMetadataUpdate}
            disabled={disabled}
          />
          <ChatWebSocketHttpFirstConnectionFields
            metadata={simulationMetadata}
            onMetadataUpdate={onMetadataUpdate}
            disabled={disabled}
          />
        </>
      )}

      {!hideAdvancedSettings && (
        <BehaviorFields
          metadata={simulationMetadata}
          onMetadataUpdate={onMetadataUpdate}
          disabled={disabled}
        />
      )}

      {!hideAdvancedSettings && (
        <Accordion
          type="single"
          collapsible
          aria-label="Advanced Configuration"
        >
          <AccordionItem value="advanced-config">
            <AccordionTrigger>
              <div className="flex flex-col items-start gap-0.5 text-left">
                <span>Advanced Configuration</span>
                <span className="text-xs text-coval-text-muted font-normal">
                  Text message format, handshake, and direction filtering
                </span>
              </div>
            </AccordionTrigger>
            <AccordionContent>
              <ChatWebSocketAdvancedFields
                metadata={simulationMetadata}
                onMetadataUpdate={onMetadataUpdate}
                disabled={disabled}
              />
            </AccordionContent>
          </AccordionItem>
        </Accordion>
      )}
    </div>
  )
}
